"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Loader, ShieldCheck, Stethoscope, Clock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICES = ["Pediatric Care", "Women’s Health", "Chronic Disease Management", "Preventive Screenings", "Minor Urgent Care", "Vaccinations", "Mental Health Support", "Senior Wellness", "Nutritional Counseling", "General Physicals"];
const LOCATIONS = ["Central Freetown", "Brookfields", "Aberdeen", "Murray Town", "Lumley", "Kissy", "Wellington", "Wilberforce", "Hill Station", "Regent"];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="87617" className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-16 bg-[hsl(150,60%,94%)] text-[hsl(174,42%,45%)] rounded-full flex items-center justify-center mb-4">
          <ShieldCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-[hsl(222,47%,11%)]">Message Sent Successfully</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-[hsl(174,42%,45%)] text-[hsl(174,42%,45%)] hover:bg-[hsl(150,60%,94%)]">
          Send Another Message
        </Button>
      </div>;
  }
  const inputBaseClass = "flex h-12 w-full rounded-md border border-[hsl(210,40%,90%)] bg-white px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(174,42%,45%)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-[hsl(222,47%,11%)] transition-all duration-200";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Full Name *</FormLabel>
              <FormControl>
                <input type="text" placeholder="Amina Sesay" className={inputBaseClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Email Address *</FormLabel>
              <FormControl>
                <input type="email" placeholder="amina@example.sl" className={inputBaseClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required",
        pattern: {
          value: /^[0-9\-\+\s\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Phone Number *</FormLabel>
              <FormControl>
                <input type="tel" placeholder="443-653-0459" className={inputBaseClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Preferred Appointment Date</FormLabel>
              <FormControl>
                <input type="date" className={inputBaseClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Service of Interest *</FormLabel>
              <FormControl>
                <select className={inputBaseClass} {...field}>
                  <option value="" disabled>Select a service</option>
                  {SERVICES.map((service, i) => <option key={service} value={service} data-index={i}>
                      {service}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location" rules={{
        required: "Please select a location"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Preferred Clinic *</FormLabel>
              <FormControl>
                <select className={inputBaseClass} {...field}>
                  <option value="" disabled>Select a location</option>
                  {LOCATIONS.map((location, i) => <option key={location} value={location} data-index={i}>
                      {location}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(222,47%,11%)] font-medium">Any Specific Health Concerns?</FormLabel>
            <FormControl>
              <textarea placeholder="Briefly describe how we can help you today..." className={`${inputBaseClass} min-h-[120px] resize-y`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-bold rounded-full bg-gradient-to-r from-[hsl(174,42%,45%)] to-[hsl(150,60%,55%)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Message...
          </> : 'Send Message'}
      </Button>
      <p className="text-xs text-center text-muted-foreground mt-4 font-sans">
        Your privacy is our priority. All medical inquiries are handled with strict confidentiality.
      </p>
    </Form>;
}
export default function ConsultationRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-in-touch" className="relative py-20 lg:py-32 overflow-hidden bg-[hsl(210,40%,98%)]">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: 'radial-gradient(circle at center, hsl(174,42%,45%) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }}></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="bg-white rounded-[32px] shadow-2xl overflow-hidden border border-[hsl(210,40%,90%)] grid grid-cols-1 lg:grid-cols-12 gap-0">
          
          <div className="lg:col-span-5 bg-[hsl(174,42%,45%)] text-white p-8 sm:p-12 md:p-16 space-y-10">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight mb-6 leading-tight">
                Compassionate Care for Your Family
              </h2>
              <p className="text-lg md:text-xl text-white/90 leading-relaxed font-sans">
                Whether it's a routine check-up or specialized care, Freetown Family Clinic is here to support your journey to long-term wellness.
              </p>
            </div>

            <div className="space-y-8 pt-4">
              <div className="flex items-start gap-5">
                <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-sm">
                  <ShieldCheck className="size-7 text-[hsl(43,89%,58%)]" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-semibold mb-1">Our Clinic Location</h4>
                  <p className="text-white/80 text-sm leading-relaxed font-sans">100 Owings Court Suite 8<br />Reisterstown Maryland 21136, USA</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-sm">
                  <Stethoscope className="size-7 text-[hsl(43,89%,58%)]" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-semibold mb-1">Call Our Medical Team</h4>
                  <p className="text-white/80 text-sm leading-relaxed font-sans">
                    Phone: 443-653-0459<br />
                    Fax: 4107027470<br />
                    Mon-Sat: 9:00 AM - 6:00 PM
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-sm">
                  <Clock className="size-7 text-[hsl(43,89%,58%)]" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-semibold mb-1">Email Inquiries</h4>
                  <p className="text-white/80 text-sm leading-relaxed font-sans">freetownfamilyclinic@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="size-14 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-sm">
                  <Stethoscope className="size-7 text-[hsl(43,89%,58%)]" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-semibold mb-1">Our Medical Team</h4>
                  <div className="text-white/80 text-sm leading-relaxed font-sans space-y-1">
                    <p>• Gibrilla Sannoh (Proprietor and Medical Director)</p>
                    <p>• Dr. Victor Conteh (Medical Operations Manager)</p>
                    <p>• Dr. Linda Ukwu (Medical staff)</p>
                    <p>• Alimatu Koroma, NP (Medical staff)</p>
                    <p>• Omolara Amosu, NP (Medical staff)</p>
                    <p>• Fatima Turay (Office Manager)</p>
                    <p>• Mr. ABU KOROMA (Clinical Aid)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 p-8 sm:p-12 md:p-16 bg-white">
            <div className="mb-10">
              <h3 className="text-3xl font-serif font-bold text-[hsl(222,47%,11%)] mb-3">Book an Appointment</h3>
              <p className="text-muted-foreground text-lg font-sans">Fill out the form below and our coordinators will reach out to confirm your visit.</p>
            </div>
            
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Consultation Request" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            service: "",
            location: "",
            preferred_date: "",
            message: ""
          }} submitText="Send Message" successMessage="Thank you for reaching out. A member of the Freetown Family Clinic team will contact you shortly to confirm your appointment details.">
              <ConsultationForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}